import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

const BrandingContext = createContext();

/**
 * useStaticFirstFrame — returns a static (non-animated) version of an image URL.
 * For GIF URLs, draws the first frame onto an off-screen canvas and returns a PNG data URL.
 * Non-GIF URLs are passed through unchanged.
 */
const useStaticFirstFrame = (url) => {
  const [staticUrl, setStaticUrl] = useState(url);

  useEffect(() => {
    if (!url) { setStaticUrl(null); return; }

    const isGif = /\.gif(\?|$)/i.test(url);
    if (!isGif) { setStaticUrl(url); return; }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        setStaticUrl(canvas.toDataURL('image/png'));
      } catch {
        // CORS or other error — fall back to the original animated URL
        setStaticUrl(url);
      }
    };
    img.onerror = () => setStaticUrl(url);
    img.src = url;
  }, [url]);

  return staticUrl;
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

/**
 * Builds a full URL for a branding asset stored in S3.
 * The asset_path is the relative S3 path stored in global_settings
 * (e.g. "_branding/logo.png"). The tenant slug is included in the URL
 * so the backend can resolve the correct S3 prefix without relying on
 * the tenant middleware (browser img/link tags can't send custom headers).
 */
const buildAssetUrl = (assetPath, tenantSlug) => {
  if (!assetPath || !tenantSlug) return null;
  return `${config.api.baseUrl}/api/branding/asset/${tenantSlug}/${assetPath}`;
};

/**
 * Injects branding colors as CSS custom properties on :root.
 * Maps DB keys → CSS variable names used by tailwind.config.js.
 *
 * Migration: new keys (border_color, chat_bg_color, etc.) may not exist
 * in older DBs. We only set a CSS var if the value is truthy — the
 * tailwind fallback in var(--color-X, #default) handles the rest.
 */
const injectCssVars = (branding) => {
  const root = document.documentElement;

  // DB key → CSS custom property name
  const colorMap = {
    '--color-primary':     branding.primary_color,
    '--color-accent':      branding.accent_color,
    '--color-page':        branding.background_color,
    '--color-surface':     branding.secondary_color,
    '--color-tile':        branding.tile_color,
    '--color-foreground':  branding.headline_color,
    '--color-border':      branding.border_color,
    '--color-chat-bg':     branding.chat_bg_color,
    '--color-chat-bubble': branding.chat_bubble_color,
    '--color-chat-text':   branding.chat_text_color,
  };

  Object.entries(colorMap).forEach(([varName, value]) => {
    if (value) {
      root.style.setProperty(varName, value);
    }
  });
};


export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Fetch branding config from backend on mount
  const fetchBranding = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/api/branding`);
      if (!response.ok) throw new Error('Failed to fetch branding');
      const data = await response.json();
      setBranding(data);

      // Inject CSS vars whenever branding data arrives
      injectCssVars(data);

      // Document title is now set by App.js based on tenant/platform context.
      // BrandingContext exposes appTitle for App.js to use.

      // Dynamically update manifest link to point to backend
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        manifestLink.href = `${config.api.baseUrl}/api/manifest.json`;
      }

      // Dynamically update favicon if configured
      if (data.favicon_url) {
        const faviconLink = document.querySelector('link[rel="icon"]');
        const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
        const faviconFullUrl = buildAssetUrl(data.favicon_url, data._tenant_slug);
        if (faviconLink) faviconLink.href = faviconFullUrl;
        if (appleTouchIcon) appleTouchIcon.href = faviconFullUrl;
      }

      // Update theme-color meta tag — reuse primary_color
      if (data.primary_color) {
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) themeColorMeta.content = data.primary_color;
      }

      setIsLoaded(true);
    } catch (e) {
      console.error('Failed to load branding:', e);
      setError(e.message);
      // Still mark as loaded so the app renders (just without branding)
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Widget script embeds on demo-preview inherit document lang via getPageLanguage().
  // App.js keeps <html lang> in sync with the active UI language; avoid overwriting
  // that here with default_language only.

  // Helper to get a welcome message for the current language.
  // Falls back to the default language if the current language has no message.
  const getWelcomeMessage = (language) => {
    const primary = branding[`welcome_message_${language}`];
    if (primary) return primary;
    const fallbackLang = branding.default_language || 'en';
    return branding[`welcome_message_${fallbackLang}`] || '';
  };

  // Helper to get asset URLs — tenant slug is embedded in the URL path
  const slug = branding._tenant_slug;
  const logoUrl = buildAssetUrl(branding.logo_url, slug);
  const avatarUrl = buildAssetUrl(branding.assistant_avatar_url, slug);
  const faviconUrl = buildAssetUrl(branding.favicon_url, slug);

  // Static (first-frame) version of the avatar — GIFs won't animate,
  // used for in-chat message avatars while header/previews stay animated.
  const staticAvatarUrl = useStaticFirstFrame(avatarUrl);

  // Optimistically update a single branding key in the local state without
  // refetching from the API. Used by saveBrandingKey in ChatWidgetSettings so
  // that the context stays in sync with what was persisted — this way, if the
  // component remounts (section collapse/expand, navigation), the useEffect
  // merge picks up the saved value instead of the stale pre-save snapshot.
  const updateBrandingKey = useCallback((key, value) => {
    setBranding(prev => ({ ...prev, [key]: value }));
  }, []);

  const value = {
    branding,
    isLoaded,
    error,
    // Re-fetch after admin saves branding changes
    refreshBranding: fetchBranding,
    // Patch a single key without full refetch (avoids race conditions with
    // debounced saves that refreshBranding would cause)
    updateBrandingKey,

    // Convenience getters
    brandName: branding.brand_name || '',
    companyName: branding.company_name || '',
    appTitle: branding.app_title || '',
    logoUrl,
    avatarUrl,
    staticAvatarUrl,
    faviconUrl,
    getWelcomeMessage,

    // GIF animation config — controls how the avatar GIF loops in the widget
    gifLoop: branding.assistant_avatar_gif_loop !== 'false',
    gifPause: parseFloat(branding.assistant_avatar_gif_pause) || 0,

    // Language config
    enableMultilang: branding.enable_multilang === 'true',
    supportedLanguages: branding.supported_languages ? 
      (typeof branding.supported_languages === 'string' && branding.supported_languages.startsWith('[') 
        ? JSON.parse(branding.supported_languages) 
        : branding.supported_languages.split(',').map(l => l.trim()).filter(Boolean))
      : [],
    defaultLanguage: branding.default_language || 'en',
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};
